import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'
import { loadJSON, saveJSON } from './storage'
import { isPivotAggregation, type PivotAggregation } from './pivot'

export type DashboardWidgetType = 'pivot'

export type DateGroup = 'day' | 'week' | 'month' | 'quarter' | 'year'

const isDateGroup = (value: unknown): value is DateGroup => (
  value === 'day' || value === 'week' || value === 'month' || value === 'quarter' || value === 'year'
)

export type ChartType = 'bar' | 'line'

const isChartType = (value: unknown): value is ChartType => (
  value === 'bar' || value === 'line'
)

export type WidgetWidth = 'narrow' | 'half' | 'full'

// v2.2 findings.md #47: 'total' (the default, and every older widget) plots
// one series of row totals; 'byColumn' plots one series per column key —
// e.g. one line per lot — when the widget has a column field.
export type ChartSeriesMode = 'total' | 'byColumn'

const isChartSeriesMode = (value: unknown): value is ChartSeriesMode => (
  value === 'total' || value === 'byColumn'
)

const isWidgetWidth = (value: unknown): value is WidgetWidth => (
  value === 'narrow' || value === 'half' || value === 'full'
)

// Grid position/size in react-grid-layout units (a 12-column grid). Absent
// on any widget that predates the real drag/resize pass — those fall back
// to an auto-placed layout derived from `width` (or full-width) at render
// time, computed by the screen, never persisted here until the operator
// actually drags or resizes that widget.
export type WidgetLayout = {
  x: number
  y: number
  w: number
  h: number
}

const isFiniteNonNegative = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0
)

const isPositiveInt = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 1
)

const isWidgetLayout = (value: unknown): value is WidgetLayout => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return isFiniteNonNegative(record.x) && isFiniteNonNegative(record.y) &&
    isPositiveInt(record.w) && isPositiveInt(record.h)
}

export type PivotWidgetV1 = {
  id: string
  type: 'pivot'
  title: string
  reportId: string
  rowField: string
  rowDateGroup?: DateGroup
  columnField?: string
  columnDateGroup?: DateGroup
  aggregation: PivotAggregation
  valueField?: string
  // v2.2 findings.md #46: extra inputs for the newer aggregations — see
  // `PivotConfig` in pivot.ts. All optional; absent on every older widget.
  orderField?: string
  entityField?: string
  denominatorField?: string
  weightField?: string
  ratioMultiplier?: number
  chartSeries?: ChartSeriesMode
  showChart: boolean
  // Defaults to 'bar' when absent, so every widget saved before this field
  // existed keeps rendering exactly as it did.
  chartType?: ChartType
  // Defaults to 'full' when absent (Panel grid pass) — matches the
  // full-width vertical stack every pre-existing widget already rendered as.
  // Superseded by `layout` once real drag/resize is used on a widget, but
  // kept so `layout` can be auto-derived from it for widgets that never
  // were.
  width?: WidgetWidth
  layout?: WidgetLayout
  createdAt: string
  updatedAt: string
}

export type DashboardDefinitionV1 = {
  version: 1
  id: string
  tenantKey: string
  name: string
  widgets: PivotWidgetV1[]
  createdAt: string
  updatedAt: string
}

type DashboardPayloadV1 = {
  version: 1
  dashboard: DashboardDefinitionV1
}

const DASHBOARD_STORAGE_KEY = 'dashboard:v1'
const DEFAULT_DASHBOARD_ID = 'main'

const dashboardKey = (tenantKey = getActiveTenantKey()): string => (
  getTenantScopedKey(DASHBOARD_STORAGE_KEY, tenantKey)
)

const nowIso = (): string => new Date().toISOString()

const normalizePivotWidget = (candidate: unknown, fallbackCreatedAt: string): PivotWidgetV1 | null => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null
  const record = candidate as Record<string, unknown>
  if (record.type !== 'pivot') return null

  const id = typeof record.id === 'string' ? record.id.trim() : ''
  const title = typeof record.title === 'string' ? record.title.trim() : ''
  const reportId = typeof record.reportId === 'string' ? record.reportId.trim() : ''
  const rowField = typeof record.rowField === 'string' ? record.rowField.trim() : ''
  if (!id || !reportId || !rowField) return null

  const aggregation = isPivotAggregation(record.aggregation) ? record.aggregation : 'count'
  const optionalField = (value: unknown): string | undefined => (
    typeof value === 'string' && value.trim() ? value.trim() : undefined
  )
  const ratioMultiplier = typeof record.ratioMultiplier === 'number' && Number.isFinite(record.ratioMultiplier)
    ? record.ratioMultiplier
    : undefined

  const columnField = typeof record.columnField === 'string' ? record.columnField.trim() : ''
  const valueField = typeof record.valueField === 'string' ? record.valueField.trim() : ''

  const rowDateGroup = isDateGroup(record.rowDateGroup) ? record.rowDateGroup : undefined
  const columnDateGroup = isDateGroup(record.columnDateGroup) ? record.columnDateGroup : undefined

  const createdAt = typeof record.createdAt === 'string' ? record.createdAt : fallbackCreatedAt
  const updatedAt = typeof record.updatedAt === 'string' ? record.updatedAt : fallbackCreatedAt

  const chartType = isChartType(record.chartType) ? record.chartType : undefined
  const width = isWidgetWidth(record.width) ? record.width : undefined
  const layout = isWidgetLayout(record.layout) ? record.layout : undefined

  return {
    id,
    type: 'pivot',
    title: title || 'Pivot',
    reportId,
    rowField,
    rowDateGroup,
    columnField: columnField || undefined,
    columnDateGroup,
    aggregation,
    valueField: valueField || undefined,
    orderField: optionalField(record.orderField),
    entityField: optionalField(record.entityField),
    denominatorField: optionalField(record.denominatorField),
    weightField: optionalField(record.weightField),
    ratioMultiplier,
    chartSeries: isChartSeriesMode(record.chartSeries) ? record.chartSeries : undefined,
    showChart: Boolean(record.showChart),
    chartType,
    width,
    layout,
    createdAt,
    updatedAt
  }
}

export const createDashboardWidgetId = (): string => {
  const cryptoObj = (globalThis as any).crypto as Crypto | undefined
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID()
  }

  const rand = Math.random().toString(16).slice(2)
  return `widget-${Date.now().toString(16)}-${rand}`
}

const createEmptyDashboard = (tenantKey: string): DashboardDefinitionV1 => {
  const timestamp = nowIso()
  return {
    version: 1,
    id: DEFAULT_DASHBOARD_ID,
    tenantKey,
    name: 'Dashboard',
    widgets: [],
    createdAt: timestamp,
    updatedAt: timestamp
  }
}

export const loadDashboard = (tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  const fallback = createEmptyDashboard(tenantKey)
  const payload = loadJSON<DashboardPayloadV1>(dashboardKey(tenantKey), { version: 1, dashboard: fallback })

  if (payload?.version !== 1) return fallback
  const dash = payload.dashboard
  if (dash?.version !== 1) return fallback

  const normalizedWidgets = Array.isArray(dash.widgets)
    ? dash.widgets
      .map((widget) => normalizePivotWidget(widget, fallback.createdAt))
      .filter((widget): widget is PivotWidgetV1 => Boolean(widget))
    : []

  return {
    ...fallback,
    ...dash,
    tenantKey,
    widgets: normalizedWidgets
  }
}

/**
 * v2.2 findings.md #49/#67: one-time upgrade of the default widgets that
 * already exist for a tenant. The seeds above now break down by `lotUnit`
 * ("Lot 1 · A3"); older copies keyed on the plain `lot` name (same-name lots
 * in different units merged, and the stock total kept one of them) or, for
 * SGR/FCR/SFR, averaged every lot into one line. A widget is only changed
 * while its pivot settings still equal what was originally seeded — any
 * widget the operator edited is left exactly as it is. Idempotent.
 */
type SeededWidgetUpgrade = {
  id: string
  matches: (widget: PivotWidgetV1) => boolean
  apply: (widget: PivotWidgetV1) => PivotWidgetV1
}

const SEEDED_FCR_WIDGET_TITLES: Record<string, string> = {
  'FCR al cosechar, por lote': 'eFCR al cosechar, por lote',
  'FCR en curso, por lote': 'eFCR en curso, por lote',
  'FCR at harvest, by lot': 'eFCR at harvest, by lot',
  'FCR in flight, by lot': 'eFCR in flight, by lot'
}

const SEEDED_WIDGET_LOT_UNIT_UPGRADES: SeededWidgetUpgrade[] = [
  ...['seed-widget-lot-growth-sgr-v1', 'seed-widget-lot-growth-fcr-v1', 'seed-widget-lot-growth-sfr-v1'].map((id) => ({
    id,
    matches: (widget: PivotWidgetV1) => widget.rowField === 'date' && widget.rowDateGroup === 'week'
      && !widget.columnField && widget.aggregation === 'avg' && (widget.chartSeries ?? 'total') === 'total',
    apply: (widget: PivotWidgetV1): PivotWidgetV1 => ({ ...widget, columnField: 'lotUnit', chartSeries: 'byColumn' })
  })),
  {
    id: 'seed-widget-core-mortality-rate-v1',
    matches: (widget) => widget.aggregation === 'ratio' && widget.columnField === 'lot',
    apply: (widget) => ({ ...widget, columnField: 'lotUnit' })
  },
  {
    id: 'seed-widget-core-avg-weight-v1',
    matches: (widget) => widget.aggregation === 'weightedAvg' && widget.columnField === 'lot',
    apply: (widget) => ({ ...widget, columnField: 'lotUnit' })
  },
  {
    id: 'seed-widget-core-stock-v1',
    matches: (widget) => widget.aggregation === 'sumOfLast' && widget.entityField === 'lot' && !widget.columnField,
    apply: (widget) => ({ ...widget, entityField: 'lotUnit' })
  },
  // v2.2 findings.md #215 (user 2026-10-26): the two seeded FCR widgets show
  // eFCR. Only a widget still on its seeded ratio is moved; its title is
  // renamed only while it is still the seeded title.
  ...([
    ['seed-widget-fcr-harvest-by-lot-v1', 'feedConversionRatio'],
    ['seed-widget-fcr-inflight-by-lot-v1', 'feedConversionRatioInFlight']
  ] as const).map(([id, seededField]) => ({
    id,
    matches: (widget: PivotWidgetV1) => widget.valueField === seededField && widget.aggregation === 'avg',
    apply: (widget: PivotWidgetV1): PivotWidgetV1 => ({
      ...widget,
      valueField: 'economicFcr',
      title: SEEDED_FCR_WIDGET_TITLES[widget.title] ?? widget.title
    })
  }))
]

export const upgradeSeededWidgetsToLotUnit = (tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  const dash = loadDashboard(tenantKey)
  let changed = false
  const widgets = dash.widgets.map((widget) => {
    const upgrade = SEEDED_WIDGET_LOT_UNIT_UPGRADES.find((entry) => entry.id === widget.id)
    if (!upgrade || widget.type !== 'pivot' || !upgrade.matches(widget)) return widget
    changed = true
    return upgrade.apply(widget)
  })
  if (!changed) return dash
  return saveDashboard({ ...dash, widgets }, tenantKey)
}

export const saveDashboard = (dashboard: DashboardDefinitionV1, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  const next: DashboardDefinitionV1 = {
    ...dashboard,
    version: 1,
    tenantKey,
    updatedAt: nowIso()
  }

  saveJSON<DashboardPayloadV1>(dashboardKey(tenantKey), { version: 1, dashboard: next })
  return next
}

export const upsertPivotWidget = (input: {
  id?: string
  title: string
  reportId: string
  rowField: string
  rowDateGroup?: DateGroup
  columnField?: string
  columnDateGroup?: DateGroup
  aggregation: PivotAggregation
  valueField?: string
  orderField?: string
  entityField?: string
  denominatorField?: string
  weightField?: string
  // `null` clears; `undefined` keeps the existing value (see below).
  ratioMultiplier?: number | null
  chartSeries?: ChartSeriesMode
  showChart: boolean
  chartType?: ChartType
  width?: WidgetWidth
}, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  const dash = loadDashboard(tenantKey)
  const timestamp = nowIso()
  const existing = input.id ? dash.widgets.find((w) => w.id === input.id) : undefined

  const widget: PivotWidgetV1 = {
    id: input.id ?? createDashboardWidgetId(),
    type: 'pivot',
    title: input.title.trim() || 'Pivot',
    reportId: input.reportId,
    rowField: input.rowField,
    rowDateGroup: isDateGroup(input.rowDateGroup) ? input.rowDateGroup : undefined,
    columnField: input.columnField?.trim() || undefined,
    columnDateGroup: isDateGroup(input.columnDateGroup) ? input.columnDateGroup : undefined,
    aggregation: input.aggregation,
    valueField: input.valueField?.trim() || undefined,
    // v2.2 findings.md #46: `undefined` keeps the existing value, `''`
    // clears it — so a caller that doesn't know about these fields (e.g.
    // an edit form predating them) can't silently strip a `sumOfLast`
    // widget's entity field and break it.
    orderField: input.orderField !== undefined ? (input.orderField.trim() || undefined) : existing?.orderField,
    entityField: input.entityField !== undefined ? (input.entityField.trim() || undefined) : existing?.entityField,
    denominatorField: input.denominatorField !== undefined ? (input.denominatorField.trim() || undefined) : existing?.denominatorField,
    weightField: input.weightField !== undefined ? (input.weightField.trim() || undefined) : existing?.weightField,
    ratioMultiplier: typeof input.ratioMultiplier === 'number' && Number.isFinite(input.ratioMultiplier)
      ? input.ratioMultiplier
      : (input.ratioMultiplier === null ? undefined : existing?.ratioMultiplier),
    chartSeries: isChartSeriesMode(input.chartSeries) ? input.chartSeries : existing?.chartSeries,
    showChart: Boolean(input.showChart),
    chartType: isChartType(input.chartType) ? input.chartType : undefined,
    width: isWidgetWidth(input.width) ? input.width : undefined,
    // The create/edit form never sets grid position/size directly — only
    // dragging or resizing does, via `setWidgetLayouts`. Editing a widget's
    // fields must not reset where it sits on the grid.
    layout: existing?.layout,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp
  }

  const nextWidgets = dash.widgets.filter((w) => w.id !== widget.id)
  nextWidgets.unshift(widget)

  return saveDashboard({ ...dash, widgets: nextWidgets }, tenantKey)
}

export const deleteWidget = (id: string, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  const dash = loadDashboard(tenantKey)
  const next = dash.widgets.filter((widget) => widget.id !== id)
  return saveDashboard({ ...dash, widgets: next }, tenantKey)
}

// Real drag/resize pass: persists every widget's grid position/size in one
// batch — react-grid-layout's onLayoutChange always hands back the full
// layout (every widget may have shifted after one item's move, since the
// compactor repacks around it), so writing widget-by-widget would both be
// more calls and risk interleaving with a stale read. Widgets not present
// in `entries` (shouldn't happen — the caller derives entries from the same
// widget list) keep whatever layout they already had.
export const setWidgetLayouts = (
  entries: Array<{ id: string; layout: WidgetLayout }>,
  tenantKey = getActiveTenantKey()
): DashboardDefinitionV1 => {
  const dash = loadDashboard(tenantKey)
  const layoutById = new Map(entries.map((entry) => [entry.id, entry.layout] as const))

  const nextWidgets = dash.widgets.map((widget) => {
    const layout = layoutById.get(widget.id)
    return layout ? { ...widget, layout } : widget
  })

  return saveDashboard({ ...dash, widgets: nextWidgets }, tenantKey)
}

// 2026-09-12 pack (item b): "first-class saved graphs" for the two
// lot-cost-per-kg saved reports — an average-FCR-by-lot bar chart per
// report, seeded under well-known ids so this is idempotent the same way
// `seedDefaultReportsIfEmpty` is. Each widget only gets created once its
// target report actually exists (the caller passes the report ids after
// `seedDefaultReportsIfEmpty` has run) — a widget pointing at a
// not-yet-seeded report would just render empty.
const FCR_HARVEST_BY_LOT_WIDGET_ID = 'seed-widget-fcr-harvest-by-lot-v1'
export const FCR_INFLIGHT_BY_LOT_WIDGET_ID = 'seed-widget-fcr-inflight-by-lot-v1'

export const seedLotCostPerKgWidgetsIfMissing = (args: {
  harvestReportId: string
  harvestReportExists: boolean
  harvestWidgetTitle: string
  inFlightReportId: string
  inFlightReportExists: boolean
  inFlightWidgetTitle: string
}, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  let dash = loadDashboard(tenantKey)

  const hasHarvestWidget = dash.widgets.some((w) => w.id === FCR_HARVEST_BY_LOT_WIDGET_ID)
  if (!hasHarvestWidget && args.harvestReportExists) {
    dash = upsertPivotWidget({
      id: FCR_HARVEST_BY_LOT_WIDGET_ID,
      title: args.harvestWidgetTitle,
      reportId: args.harvestReportId,
      rowField: 'lotKey',
      aggregation: 'avg',
      valueField: 'economicFcr',
      showChart: true
    }, tenantKey)
  }

  const hasInFlightWidget = dash.widgets.some((w) => w.id === FCR_INFLIGHT_BY_LOT_WIDGET_ID)
  if (!hasInFlightWidget && args.inFlightReportExists) {
    dash = upsertPivotWidget({
      id: FCR_INFLIGHT_BY_LOT_WIDGET_ID,
      title: args.inFlightWidgetTitle,
      reportId: args.inFlightReportId,
      rowField: 'lotKey',
      aggregation: 'avg',
      valueField: 'economicFcr',
      showChart: true
    }, tenantKey)
  }

  return dash
}

// PRD `docs/reports-panel-fcr-sgr-sfr-timeseries-prd.md` item (c). Three
// single-metric widgets (decision 4: separate widgets, not one combined
// multi-series widget — a deferred fast-follow) against the one
// `lotGrowthPerformance` report — same idempotent, "only seed once the
// target report exists" pattern as `seedLotCostPerKgWidgetsIfMissing`
// above. Weekly line charts: `rowDateGroup: 'week'` + `chartType: 'line'`
// match the cadence and shape Feed.tsx's own SGR/FCR/SFR chart already
// uses; `aggregation: 'avg'` is the correct rollup for a ratio (not 'sum').
export const LOT_GROWTH_SGR_WIDGET_ID = 'seed-widget-lot-growth-sgr-v1'
export const LOT_GROWTH_FCR_WIDGET_ID = 'seed-widget-lot-growth-fcr-v1'
export const LOT_GROWTH_SFR_WIDGET_ID = 'seed-widget-lot-growth-sfr-v1'

export const seedLotGrowthPerformanceWidgetsIfMissing = (args: {
  reportId: string
  reportExists: boolean
  sgrWidgetTitle: string
  fcrWidgetTitle: string
  sfrWidgetTitle: string
}, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  let dash = loadDashboard(tenantKey)
  if (!args.reportExists) return dash

  const seedOne = (id: string, title: string, valueField: string): void => {
    if (dash.widgets.some((w) => w.id === id)) return
    dash = upsertPivotWidget({
      id,
      title,
      reportId: args.reportId,
      rowField: 'date',
      rowDateGroup: 'week',
      // v2.2 findings.md #49/#67: one line per lot ("Lote · Unidad"), as the
      // titles say — SGR/FCR/SFR of a 12 g lot and an 861 g lot must not be
      // averaged into one line.
      columnField: 'lotUnit',
      chartSeries: 'byColumn',
      aggregation: 'avg',
      valueField,
      showChart: true,
      chartType: 'line'
    }, tenantKey)
  }

  seedOne(LOT_GROWTH_SGR_WIDGET_ID, args.sgrWidgetTitle, 'sgrPercent')
  seedOne(LOT_GROWTH_FCR_WIDGET_ID, args.fcrWidgetTitle, 'fcrPlanned')
  seedOne(LOT_GROWTH_SFR_WIDGET_ID, args.sfrWidgetTitle, 'sfrPercent')

  return dash
}

// v2.2 findings.md #45 (reworked after #46/#47 landed): the three GAP/ASC/
// BAP P0 streams not already covered by the FCR/SGR seeds above —
// mortality, stock and average weight over time — against the one
// `seed-core-monitoring-v1` report (`productionDatatable`). Same
// idempotent, "only seed once the target report exists", never-overwrite
// pattern and weekly line-chart shape as
// `seedLotGrowthPerformanceWidgetsIfMissing`, but each with the
// aggregation that's actually correct for the quantity:
// - mortality: `ratio` Σ daily deaths / Σ derived stock × 100, one line
//   per lot — weights each day by its stock instead of an unweighted mean
//   of per-row percentages;
// - stock: `sumOfLast` by lot — each lot's latest count in the week,
//   summed into a farm total (stock is a level; a weekly `sum` would add
//   ~7 daily rows per lot);
// - weight: `weightedAvg` by derived stock, one line per lot — a new 12 g
//   lot and an 861 g lot are never averaged into one line, and a weight
//   row with no stock (e.g. a stray sample) can't skew it.
export const CORE_MORTALITY_RATE_WIDGET_ID = 'seed-widget-core-mortality-rate-v1'
export const CORE_STOCK_WIDGET_ID = 'seed-widget-core-stock-v1'
export const CORE_AVG_WEIGHT_WIDGET_ID = 'seed-widget-core-avg-weight-v1'

export const seedCoreMonitoringWidgetsIfMissing = (args: {
  reportId: string
  reportExists: boolean
  mortalityRateWidgetTitle: string
  stockWidgetTitle: string
  avgWeightWidgetTitle: string
}, tenantKey = getActiveTenantKey()): DashboardDefinitionV1 => {
  let dash = loadDashboard(tenantKey)
  if (!args.reportExists) return dash

  const weeklyLine = {
    reportId: args.reportId,
    rowField: 'date',
    rowDateGroup: 'week' as const,
    showChart: true,
    chartType: 'line' as const
  }

  const seedOne = (input: Parameters<typeof upsertPivotWidget>[0] & { id: string }): void => {
    if (dash.widgets.some((w) => w.id === input.id)) return
    dash = upsertPivotWidget(input, tenantKey)
  }

  seedOne({
    ...weeklyLine,
    id: CORE_MORTALITY_RATE_WIDGET_ID,
    title: args.mortalityRateWidgetTitle,
    columnField: 'lotUnit',
    chartSeries: 'byColumn',
    aggregation: 'ratio',
    valueField: 'dailyMortality',
    denominatorField: 'fishStockDerived',
    ratioMultiplier: 100
  })
  seedOne({
    ...weeklyLine,
    id: CORE_STOCK_WIDGET_ID,
    title: args.stockWidgetTitle,
    aggregation: 'sumOfLast',
    valueField: 'fishStockDerived',
    entityField: 'lotUnit'
  })
  seedOne({
    ...weeklyLine,
    id: CORE_AVG_WEIGHT_WIDGET_ID,
    title: args.avgWeightWidgetTitle,
    columnField: 'lotUnit',
    chartSeries: 'byColumn',
    aggregation: 'weightedAvg',
    valueField: 'weight',
    weightField: 'fishStockDerived'
  })

  return dash
}
